import type { TFunction } from "i18next";
import type { ActivityLogChange } from "@/types/admin";

/**
 * Human-readable rendering helpers for the activity-log "Changes" column
 * (TASK-515). The audit diff arrives from the backend as
 * `{ field: [old, new] }` where both values are already string-serialized
 * (`core/src/apps/core/signals.py` `_str_safe`) — so everything here operates on
 * strings and never trusts/renders HTML.
 */

/**
 * Field names that recur across the audited Route / Order / Vehicle / Warehouse
 * models plus the admin-managed Tenant / User / settings rows. Each maps to an
 * i18n key under `adminPanel.activityLog.field.*`. Unmapped fields fall back to
 * a humanized version of the raw key, so nothing ever renders blank.
 */
const FIELD_LABEL_KEYS: Record<string, string> = {
  // Generic / shared
  name: "name",
  status: "status",
  is_active: "isActive",
  notes: "notes",
  priority: "priority",
  // Tenant
  suspension_reason: "suspensionReason",
  slug: "slug",
  country: "country",
  // Order
  address: "address",
  delivery_date: "deliveryDate",
  customer_name: "customerName",
  customer_phone: "customerPhone",
  weight: "weight",
  volume: "volume",
  time_window_start: "timeWindowStart",
  time_window_end: "timeWindowEnd",
  // Added when the order-detail change log started rendering diffs (TASK-4836):
  // these showed up as raw «Route» / «Tracking token» in the dispatcher's view.
  route: "route",
  tracking_token: "trackingToken",
  external_id: "externalId",
  customer_email: "customerEmail",
  service_time_min: "serviceTime",
  strict_time_window: "strictTimeWindow",
  delivery_type: "deliveryType",
  tags: "tags",
  goods_value: "goodsValue",
  pallets: "pallets",
  // Route
  planned_mileage_km: "plannedMileageKm",
  vehicle: "vehicle",
  driver: "driver",
  scheduled_date: "scheduledDate",
  started_at: "startedAt",
  completed_at: "completedAt",
  // User
  email: "email",
  first_name: "firstName",
  last_name: "lastName",
  role: "role",
  phone: "phone",
  // Platform / billing settings (admin_panel build_diff path)
  billing_rate: "billingRate",
  billing_currency: "billingCurrency",
  default_confirmation_radius_m: "confirmationRadius",
  custom_rate: "customRate",
  system_rate: "systemRate",
  // Sprint 180 rate fields. Without these the Activity Log renders the raw
  // snake_case field name when a superadmin changes a money value — the one
  // place a rate change is meant to be traceable.
  currency: "currency",
  dealer_cost_rate: "dealerCostRate",
  dealer_cost_rate_dealer_id: "dealerCostRateDealer",
  default_selling_rate: "defaultSellingRate",
};

/**
 * Field names that are auto-managed timestamps and carry no signal for a
 * human reader. The backend already suppresses these at serialize time
 * (TASK-3678), but we filter them defensively on the client too so legacy
 * rows (and the `Updated at` humanized form) never render as diff noise.
 *
 * Matching is case- and separator-insensitive (`updated_at`, `Updated at`,
 * `updatedAt` all collapse to `updatedat`).
 */
const SUPPRESSED_DIFF_FIELDS = new Set(["updatedat", "createdat", "modified"]);

/**
 * Whether an audit-diff field should be hidden from the Changes cell because
 * it is an auto-managed timestamp (see {@link SUPPRESSED_DIFF_FIELDS}).
 */
export function isSuppressedDiffField(field: string): boolean {
  const normalized = field.replace(/[_\s-]+/g, "").toLowerCase();
  return SUPPRESSED_DIFF_FIELDS.has(normalized);
}

/**
 * Status enum values shared across audited models (Order / Route lifecycles)
 * mapped to i18n keys under `adminPanel.activityLog.statusValue.*`. Used to
 * localize the `status` field's before/after values so a RU user does not see
 * raw English enum strings like `in_route`.
 */
const STATUS_VALUE_KEYS: Record<string, string> = {
  // Order lifecycle
  new: "new",
  in_route: "inRoute",
  visited: "visited",
  confirmed: "confirmed",
  rejected: "rejected",
  skipped: "skipped",
  archived: "archived",
  // Route lifecycle
  planned: "planned",
  active: "active",
  completed: "completed",
  history: "history",
};

/**
 * Resolve a localized label for a known status enum value, falling back to the
 * raw value when it is not a recognized status. Returns `null` when the value
 * is not a known status so callers can decide whether to localize.
 */
export function statusValueLabel(
  value: string | null,
  t: TFunction,
): string | null {
  if (value == null) return null;
  const key = STATUS_VALUE_KEYS[value.trim()];
  if (!key) return null;
  return t(`adminPanel.activityLog.statusValue.${key}`, {
    defaultValue: value,
  });
}

/**
 * Resolve a localized label for a priority level, or `null` if the value is not
 * one of the three levels.
 *
 * Sprint 186 (TASK-4833) turned `priority` from a 1..99 number into a
 * `low`/`medium`/`high` enum, so the change log started rendering raw English
 * enum values («Приоритет: medium→high») where it previously showed numbers that
 * needed no translation. Reuses `orders.priorityLevel.*` — the same keys
 * `PriorityDot` and the priority field render — rather than a second copy that
 * could disagree with them.
 *
 * A legacy audit row written BEFORE the migration still carries a number, which
 * is not a level and so returns `null`: the caller then formats it generically
 * and history stays readable instead of showing an empty cell.
 */
export function priorityValueLabel(
  value: string | null,
  t: TFunction,
): string | null {
  if (value == null) return null;
  const level = value.trim().toLowerCase();
  if (level !== "low" && level !== "medium" && level !== "high") return null;
  return t(`orders.priorityLevel.${level}`, { defaultValue: value });
}

/**
 * Turn a raw snake/camel field name into a readable label as a last resort
 * (e.g. "planned_mileage_km" → "Planned mileage km").
 */
export function humanizeFieldName(field: string): string {
  const spaced = field
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return field;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Resolve a localized human label for an audit diff field, falling back to a
 * humanized form of the raw key when the field is not in the known map.
 */
export function fieldLabel(field: string, t: TFunction): string {
  const key = FIELD_LABEL_KEYS[field];
  if (key) {
    return t(`adminPanel.activityLog.field.${key}`, {
      defaultValue: humanizeFieldName(field),
    });
  }
  return humanizeFieldName(field);
}

// Python `str(bool)` produces "True"/"False"; cover the JS-ish forms too.
const TRUE_TOKENS = new Set(["true", "True", "1"]);
const FALSE_TOKENS = new Set(["false", "False", "0"]);

/** Detects ISO-8601-ish datetime strings produced by `str(datetime)`. */
const DATETIME_RE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;
/** Detects plain date strings produced by `str(date)`. */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format a single string diff value (old or new) for display.
 *
 * - null / "None" / "" → an explicit localized empty marker.
 * - booleans → localized yes/no.
 * - dates / datetimes → browser-locale formatted.
 * - everything else → the raw string (rendered as escaped React text).
 */
export function formatDiffValue(value: string | null, t: TFunction): string {
  if (value === null || value === undefined) {
    return t("adminPanel.activityLog.valueEmpty");
  }
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "None") {
    return t("adminPanel.activityLog.valueEmpty");
  }
  if (TRUE_TOKENS.has(trimmed)) return t("adminPanel.activityLog.valueYes");
  if (FALSE_TOKENS.has(trimmed)) return t("adminPanel.activityLog.valueNo");

  if (DATETIME_RE.test(trimmed)) {
    const d = new Date(trimmed.replace(" ", "T"));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  }
  if (DATE_RE.test(trimmed)) {
    const d = new Date(`${trimmed}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }

  return value;
}

/**
 * Normalize a diff change into `[old, new]` regardless of the backend shape.
 *
 * The unified activity-log feed mixes two serializations (see
 * {@link ActivityLogChange}):
 * - signal audit → `[old, new]` tuple.
 * - admin-panel `build_diff` → `{ before, after }` object.
 *
 * The Changes cell previously only read tuple indices, so every
 * `{ before, after }` row (Tenant / User / PlatformSettings billing-rate
 * changes) rendered "—→—". This collapses both shapes to a single tuple.
 */
export function normalizeDiffChange(
  change: ActivityLogChange | null | undefined,
): [string | null, string | null] {
  if (change == null) return [null, null];
  if (Array.isArray(change)) {
    return [coerceDiffValue(change[0]), coerceDiffValue(change[1])];
  }
  return [coerceDiffValue(change.before), coerceDiffValue(change.after)];
}

/**
 * Coerce a raw diff value to the `string | null` the formatter expects.
 *
 * The backend `build_diff` serializes values as-is, so booleans (`is_active`),
 * numbers, and null all appear in `before`/`after` — not only strings. The
 * declared `ActivityLogChange` type claimed `string`, so `tsc` and the
 * string-only unit fixtures missed it, but a real `is_active` toggle ships
 * `{before: true, after: false}` and `formatDiffValue`'s `value.trim()` then
 * throws `TypeError: value.trim is not a function`, crashing the whole page.
 * Normalizing here keeps every downstream consumer safe.
 */
function coerceDiffValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "True" : "False";
  return String(value);
}
